import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PendingComponentBadge } from "@/components/PendingComponentBadge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Check, AlertTriangle, Loader2, Save, Sparkles, Wand2, CheckCheck, PackagePlus, Boxes, FileText } from "lucide-react";
import { useVehicleCategories } from "@/lib/vehicleLists";
import { setDonorCategory } from "@/lib/setDonorCategory";
import { CreatePartFromTruck, PART_NAME_FROM_SPEC } from "@/components/CreatePartFromTruck";
import { ConditionControl, ConditionBadge } from "@/components/ConditionControl";
import { ComponentWhere, ComponentListLine } from "@/components/ComponentWhere";
import { PullButton, canPullHere, pullOf } from "@/components/PullFromRow";
import { ComponentNotes } from "@/components/ComponentNotes";
import { ComponentSuggestions } from "@/components/ComponentSuggestions";
import { PresenceReviewBlock } from "@/components/PresenceReviewBlock";
import { TruckAiWorkbench } from "@/components/TruckAiWorkbench";
import { fieldList } from "@/lib/text";

const CONF = { high: "text-emerald-600", medium: "text-amber-600", low: "text-red-600" };
const srcLabel = (s) => (s === "build_sheet" ? "build sheet" : "photo");

// Per-truck structured specs + AI capture (Chunk B). AI reads photos + build sheet, drafts
// ai_suggested (amber) values; human reviews/confirms/fills. AI never overwrites manual/confirmed.
export function TruckSpecsPanel({ donorId, canEdit, donor, onCategoryChange }) {
  const { has } = useAuth();
  const navigate = useNavigate();
  const categories = useVehicleCategories();
  const presenceRef = useRef(null);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [aiOn, setAiOn] = useState(false);
  const [wbOpen, setWbOpen] = useState(false);
  const [result, setResult] = useState(null);       // last scan summary
  const [confirmAll, setConfirmAll] = useState(null); // {group} | {} for whole panel
  const [cpt, setCpt] = useState(null);               // create-part-from-spec context

  const canScan = canEdit && has("use_ai_assist");
  const canParts = has("manage_parts");

  const load = useCallback(async () => {
    try { const r = await api.get(`/donors/${donorId}/specs`); setData(r.data); setDraft({}); }
    catch { setData(null); }
  }, [donorId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/ai/status").then((r) => setAiOn(!!r.data?.available)).catch(() => setAiOn(false)); }, []);
  // THE TILE IS THE PATH INTO THE JOB, not a filtered list to work through by hand — so the
  // worklist deep-links straight into this truck and scrolls to the block that does the work.
  // IT MUST NOT DEPEND ON THE AI SWITCH: this block used to live inside the "Ask AI" dialog, so on
  // any account without `use_ai_assist`, on the read-only truck page, or with the AI master switch
  // off, the review he was sent to do simply was not on the screen (found 2026-09-25).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const presence = p.get("review") === "presence";
    const comp = p.get("component");
    if (!presence && !comp) return;
    const t = setTimeout(() => {
      const el = presence ? presenceRef.current
        : document.querySelector(`[data-testid="spec-component-${comp}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 900);
    return () => clearTimeout(t);
  }, [donorId, data]);

  // "Got the serial in front of you?" — the specs are one tap away from the review, never a gate.
  // On the read-only truck page that means the edit screen, which is where the fields live.
  const goToSpecs = (code) => {
    if (!canEdit) { navigate(`/donors/${donorId}/edit?component=${code}`, { state: { from: window.location.pathname + window.location.search } }); return; }
    document.querySelector(`[data-testid="spec-component-${code}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const setCat = async (code) => {
    try { await setDonorCategory(donorId, code); setResult(null); await load(); onCategoryChange?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const dirty = Object.keys(draft).length > 0;
  const saveAll = async () => {
    setSaving(true);
    try { const r = await api.put(`/donors/${donorId}/specs`, { values: draft }); setData(r.data); setDraft({}); toast.success("Specs saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const confirm = async (key) => {
    setBusyKey(key);
    try { const r = await api.post(`/donors/${donorId}/specs/${key}/confirm`); setData(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };
  const applyHint = async (key, value) => {
    setBusyKey(key);
    try { const r = await api.put(`/donors/${donorId}/specs`, { values: { [key]: value } }); setData(r.data); toast.success("Updated to AI value"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };

  const doConfirmAll = async () => {
    const body = { keys: confirmAll?.keys || [] };
    setConfirmAll(null);
    try { const r = await api.post(`/donors/${donorId}/specs/confirm-all`, body); setData(r.data); toast.success("Confirmed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return null;
  const activeCats = categories.filter((c) => !c.archived);
  const needsCat = data.category === "uncategorized" || data.category_needs_review;
  // ONLY VIN-DECODED values can be confirmed together — never the engine or transmission, never an AI value.
  const isDecodedPending = (f) => f.unverified && f.source === "decoded" && !f.own_tick && f.value != null && f.value !== "";
  const anyUnverified = data.groups.some((g) => g.fields.some(isDecodedPending));
  const groupUnverified = (g) => g.fields.some(isDecodedPending);
  // THE WORDS ARE THE LIST: the button names every field it would confirm, and confirms only those.
  const askFor = (fields, group) => {
    const pend = fields.filter(isDecodedPending);
    setConfirmAll({ group, keys: pend.map((f) => f.key), words: fieldList(pend.map((f) => f.label)) });
  };
  const allPending = data.groups.flatMap((g) => g.fields.filter(isDecodedPending));

  // confidence-ranked: unverified low-confidence first, then unverified, then the rest
  const rank = (f) => {
    if (!f.value && f.value !== 0 && f.value !== false) return 4;
    if (f.unverified) return f.ai_confidence === "low" ? 0 : 1;
    return 3;
  };
  const sortFields = (fs) => [...fs].sort((a, b) => rank(a) - rank(b));

  const chip = (f) => {
    if (f.value == null || f.value === "") return null;
    if (f.source === "confirmed") return <Badge data-testid={`spec-chip-${f.key}`} className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><Check className="w-3 h-3 mr-0.5" />Confirmed</Badge>;
    if (f.source === "manual") return <Badge data-testid={`spec-chip-${f.key}`} className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><Check className="w-3 h-3 mr-0.5" />Entered</Badge>;
    const low = f.ai_confidence === "low";
    const label = f.source === "ai_suggested" ? `AI · ${srcLabel(f.ai_source)}${f.ai_confidence ? ` · ${f.ai_confidence}` : ""}` : "Decoded";
    return <Badge data-testid={`spec-chip-${f.key}`} className={`${low ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"} border text-[10px]`}><AlertTriangle className="w-3 h-3 mr-0.5" />{low ? "double-check · " : ""}{label}</Badge>;
  };

  const renderInput = (f) => {
    const cur = draft[f.key] !== undefined ? draft[f.key] : (f.value ?? "");
    const set = (v) => setDraft((d) => ({ ...d, [f.key]: v }));
    if (!canEdit) return <span className="text-sm text-slate-800">{f.value ?? <span className="text-slate-300">—</span>}{f.value != null && f.unit ? ` ${f.unit}` : ""}</span>;
    if (f.type === "bool") {
      const v = cur === true || cur === "true" || cur === 1;
      return (
        <div className="flex gap-1">
          <button data-testid={`spec-val-${f.key}-yes`} onClick={() => set(true)} className={`px-3 py-1.5 rounded-lg text-xs border ${v ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}>Yes</button>
          <button data-testid={`spec-val-${f.key}-no`} onClick={() => set(false)} className={`px-3 py-1.5 rounded-lg text-xs border ${cur === false ? "border-slate-400 bg-slate-100 text-slate-700" : "border-slate-200 text-slate-500"}`}>No</button>
        </div>
      );
    }
    if (f.type === "enum") {
      return (
        <Select value={cur ? String(cur) : ""} onValueChange={set}>
          <SelectTrigger data-testid={`spec-val-${f.key}`} className="rounded-lg h-9 w-48"><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>{f.enum_options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    return <Input data-testid={`spec-val-${f.key}`} value={cur} onChange={(e) => set(e.target.value)} type={f.type === "number" ? "number" : "text"} placeholder={f.unit || "—"} className="rounded-lg h-9 w-40" />;
  };

  // one field row (reused by component blocks + other-spec groups). Whole-component spawn
  // replaces the old per-serial button, so identity numbers move together as one unit.
  const renderFieldRow = (f) => (
    <div key={f.key} data-testid={`spec-field-${f.key}`} className="flex items-center gap-3 flex-wrap py-1.5 border-b border-slate-50 last:border-0">
      <span className="w-40 shrink-0 text-sm text-slate-600">{f.label}{f.unit ? <span className="text-slate-400"> ({f.unit})</span> : ""}</span>
      <span className="flex-1 min-w-[10rem]">{renderInput(f)}</span>
      {chip(f)}
      {/* AI-01 point 7: the AI's own doubt, where the spec is actually confirmed. These two
          signals were stored and shown in the Ask AI window only — but a spec gets confirmed
          here, days later, outside that window, where the row looked certain. Same wording as
          the workbench so the two can never drift apart. */}
      {(f.ai_ambiguous || []).length > 0 && (
        <Badge data-testid={`spec-ambiguous-${f.key}`} title="The AI could not be certain of these characters — check them against the tag"
               className="bg-amber-50 text-amber-800 border border-amber-300 text-[10px] font-mono">
          <AlertTriangle className="w-3 h-3 mr-0.5" />hard-to-read: {(f.ai_ambiguous || []).join(" ")}
        </Badge>
      )}
      {f.ai_format_flag && (
        <Badge data-testid={`spec-format-${f.key}`} title={f.ai_format_flag.note}
               className="bg-amber-50 text-amber-800 border border-amber-300 text-[10px]">
          <AlertTriangle className="w-3 h-3 mr-0.5" />{f.ai_format_flag.note}
        </Badge>
      )}
      {(f.ai_vin_repairs || []).length > 0 && (
        <span data-testid={`spec-vin-repair-${f.key}`} className="inline-flex items-center gap-1 text-[11px] text-amber-800">
          <AlertTriangle className="w-3 h-3" />
          reading the <b className="font-mono">{f.ai_vin_repairs[0].was}</b> as{" "}
          <b className="font-mono">{f.ai_vin_repairs[0].now}</b> makes the check digit match:
          <b className="ml-1 font-mono">{f.ai_vin_repairs[0].vin}</b>
          {canEdit && (
            <button data-testid={`spec-vin-repair-use-${f.key}`}
                    onClick={() => applyHint(f.key, f.ai_vin_repairs[0].vin)}
                    className="underline hover:text-amber-900">use</button>
          )}
        </span>
      )}
      {(f.ai_agreement === "conflict" || f.ai_alt || f.ai_vin_conflict) && (
        <Badge data-testid={`spec-conflict-${f.key}`}
               title={f.ai_vin_conflict
                 ? "The VIN decode and what the AI read do not agree — nothing was changed; go and look"
                 : "The build sheet and a photo did not agree — the losing reading is kept"}
               className="bg-red-50 text-red-700 border border-red-200 text-[10px]">
          <AlertTriangle className="w-3 h-3 mr-0.5" />sources disagree{f.ai_alt ? <>: also read as <b className="ml-1 font-mono">{String(f.ai_alt.value)}</b></> : null}
        </Badge>
      )}
      {/* HIS ADDITION: the VIN decode is a third source and it gets the SAME flag. Nothing was
          changed — the decode's answer is shown so a person checks the real thing. */}
      {f.ai_vin_conflict && (
        <span data-testid={`spec-vin-conflict-${f.key}`}
          className="flex w-full items-center gap-1.5 text-[11px] rounded-md bg-red-50 border border-red-200 px-2 py-1 text-red-700">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          The VIN decode says <b className="font-mono">{String(f.ai_vin_conflict.value)}</b> — check the real one
          {canEdit && (
            <button data-testid={`spec-vin-conflict-use-${f.key}`}
                    onClick={() => applyHint(f.key, f.ai_vin_conflict.value)}
                    className="ml-auto underline hover:text-red-900">use the decode's</button>
          )}
        </span>
      )}
      {f.ai_agreement === "both" && (
        <Badge data-testid={`spec-agree-${f.key}`} className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">
          <Check className="w-3 h-3 mr-0.5" />Sheet + Photo agree
        </Badge>
      )}
      {f.dup_warning && <Badge data-testid={`spec-dup-${f.key}`} className="bg-red-50 text-red-700 border border-red-200 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />also on {f.dup_warning}</Badge>}
      {f.value != null && f.value !== "" && f.unverified && canEdit && (
        <Button data-testid={`spec-confirm-${f.key}`} size="sm" variant="outline" disabled={busyKey === f.key} onClick={() => confirm(f.key)} className="h-7 rounded-lg border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[11px]">
          {busyKey === f.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Confirm</>}
        </Button>
      )}
      {f.ai_hint && canEdit && (
        <span data-testid={`spec-hint-${f.key}`} className="inline-flex items-center gap-1 text-[11px] text-indigo-600">
          <Wand2 className="w-3 h-3" />AI saw <b>{String(f.ai_hint.value)}</b>
          <button onClick={() => applyHint(f.key, f.ai_hint.value)} className="underline hover:text-indigo-800">use</button>
        </span>
      )}
    </div>
  );

  const components = data.components || [];
  const compKeys = new Set(components.flatMap((c) => (c.fields || []).map((f) => f.key)));
  const descField = (data.groups || []).flatMap((g) => g.fields).find((f) => f.key === "description");
  const otherGroups = (data.groups || [])
    .map((g) => ({ group: g.group, fields: g.fields.filter((f) => !compKeys.has(f.key) && f.key !== "description") }))
    .filter((g) => g.fields.length > 0);

  return (
    <div data-testid="truck-specs-panel" className="space-y-4">
      {/* header: category + scan */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Vehicle category</span>
        {canEdit ? (
          <Select value={data.category} onValueChange={setCat}>
            <SelectTrigger data-testid="truck-category-select" className={`h-8 w-auto rounded-lg text-xs ${needsCat ? "border-amber-400 bg-amber-50 text-amber-800" : ""}`}><SelectValue /></SelectTrigger>
            <SelectContent>{activeCats.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`truck-cat-opt-${c.code}`}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        ) : <Badge className="bg-slate-100 text-slate-700">{categories.find((c) => c.code === data.category)?.name || data.category}</Badge>}
        {/* "TYPE NOT CONFIRMED", not "Needs review" (owner ruling 2026-09-25). It says what is
            actually uncertain — the vehicle type is a guess until somebody says otherwise — and it
            frees the word REVIEW for the one job he calls review: going through a truck and
            confirming what's on it. Two different things called review is the confusion he pays
            to remove. */}
        {needsCat && <Badge data-testid="truck-cat-needs-review" className="bg-amber-100 text-amber-800 text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" />Type not confirmed</Badge>}
        <span className="flex-1" />
        {canScan && aiOn && (
          <Button data-testid="ai-scan-btn" size="sm" onClick={() => setWbOpen(true)} className="rounded-lg bg-gradient-to-r from-[#B01E28] to-[#E0781A] text-white hover:opacity-90 h-8">
            <Sparkles className="w-4 h-4 mr-1" />Ask AI
          </Button>
        )}
      </div>

      {/* AI category suggestion banner */}
      {needsCat && result?.category_suggestion && canEdit && (
        <div data-testid="ai-category-suggestion" className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
          <Wand2 className="w-4 h-4" />AI suggests <b>{result.category_suggestion.name}</b> <span className="text-indigo-400 text-xs">({result.category_suggestion.confidence})</span>
          <Button data-testid="ai-use-category" size="sm" onClick={() => setCat(result.category_suggestion.code)} className="ml-auto h-7 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-xs">Use</Button>
        </div>
      )}

      {/* scan result summary + gap list */}
      {result && (result.filled > 0 || result.missing?.length > 0 || result.dedup?.length > 0) && (
        <div data-testid="ai-scan-summary" className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 space-y-1">
          <div><Sparkles className="w-3.5 h-3.5 inline text-[#E0781A] mr-1" />AI drafted <b>{result.filled}</b> field(s) from {result.images_used?.photos || 0} photo(s){result.images_used?.build_sheet ? ` + build sheet` : ""}. Review the amber ones.</div>
          {result.dedup?.map((d) => <div key={d.key} className="text-red-600">⚠ {d.label} <b>{d.value}</b> already on {d.other_stock}</div>)}
          {result.missing?.length > 0 && <div className="text-slate-500">Still missing: {result.missing.map((m) => m.label).join(", ")}</div>}
          {result.unreadable_note && <div className="text-slate-400 italic">{result.unreadable_note}</div>}
        </div>
      )}

      {/* panel-level confirm all */}
      {canEdit && anyUnverified && (
        <Button data-testid="specs-confirm-all" size="sm" variant="outline" onClick={() => askFor(allPending)} className="rounded-lg border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-8">
          <CheckCheck className="w-4 h-4 mr-1" />Confirm {fieldList(allPending.map((f) => f.label))}
        </Button>
      )}

      {/* Description — surfaced prominently (was previously buried/invisible) */}
      {descField && (
        <div data-testid="spec-description-block" className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Description</div>
          {canEdit ? (
            <textarea data-testid="spec-val-description" rows={3} placeholder="Internal description…"
              value={draft.description !== undefined ? draft.description : (descField.value ?? "")}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E0781A]/30" />
          ) : (
            <p data-testid="spec-description-value" className="text-sm text-slate-700 whitespace-pre-wrap">{descField.value || <span className="text-slate-300">— no description yet</span>}</p>
          )}
          <div className="mt-1">{chip(descField)}</div>
        </div>
      )}

      {/* COMPONENT blocks — a tag's identity grouped together, each branchable into a part ON DEMAND */}
      {components.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-baseline gap-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Components on this truck</div>
            <ComponentListLine list={data.one_list} anyBad={data.condition_summary?.any_bad} testid="specs-condition-summary" />
          </div>
          {/* A TRUCK HE SKIPPED MUST LOOK DIFFERENT FROM ONE HE CHECKED. Silence here would read
              like a clean truck, so either the review block is here (with the absence stated in
              red inside it) or, for somebody who can't rate, the bare fact is stated. */}
          <div ref={presenceRef}>
            {data.can_assess ? (
              <PresenceReviewBlock donorId={donorId} data={data} onSaved={() => load()}
                onGoToSpecs={goToSpecs} />
            ) : data.presence_review ? (
              <p data-testid="specs-presence-checked" className="text-[11px] text-emerald-700">
                <b>Reviewed</b> · {data.presence_review.by} ·{" "}
                {String(data.presence_review.at).slice(0, 10)} · {data.presence_review.present} on the
                truck{data.presence_review.gone ? ` · ${data.presence_review.gone} gone` : ""}
                {data.presence_review.cant_tell ? ` · ${data.presence_review.cant_tell} couldn't tell` : ""}
              </p>
            ) : (
              <p data-testid="specs-presence-unchecked" className="text-[11px] text-[#B01E28]">
                <b>Not reviewed yet</b> — nobody has said whether what's listed here is still
                on the truck.
              </p>
            )}
          </div>
          <ComponentSuggestions donorId={donorId} items={data.one_list?.suggestions} answered={data.one_list?.answered}
            canAnswer={!!data.can_assess} onChanged={() => load()} testid="specs-suggestions" />
          {components.map((c) => (
            <div key={c.code} data-testid={`spec-component-${c.code}`} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex flex-wrap items-center gap-2 bg-slate-50 border-b border-slate-100 px-3 py-2">
                <Boxes className="w-4 h-4 text-violet-600" />
                <span className="font-semibold text-sm text-slate-800">{c.label}</span>
                <PendingComponentBadge show={c.pending_group} testid={`spec-component-pending-${c.code}`} />
                {c.condition && <ConditionBadge row={c.condition} />}
                {c.condition?.where && <ComponentWhere where={c.condition.where} testid={`spec-where-${c.code}`} />}
                {!c.has_data && <span className="text-[10px] text-slate-400 italic">nothing captured yet</span>}
                <span className="flex-1" />
                {canParts && c.can_spawn && canPullHere(c.condition?.where) && (
                  <PullButton testid={`create-part-component-${c.code}`} onClick={() => setCpt(pullOf(c))} />
                )}
              </div>
              <div className="p-3 space-y-2">
                {/* IS IT THERE, AND IS IT ANY GOOD — beside the specs, because somebody typing
                    "500 HP Cummins" off a build sheet is exactly who knows it was pulled in 2019. */}
                {c.condition && (
                  <ConditionControl donorId={donorId} row={c.condition}
                    states={data.condition_states || []} reasons={data.condition_reasons || []}
                    canAssess={!!data.can_assess} canDismiss={!!data.can_dismiss}
                    dismissReasons={data.dismiss_reasons || []} onSaved={() => load()} />
                )}
                {c.condition && <ComponentNotes donorId={donorId} component={c.code} notes={c.condition.notes}
                  canAdd={!!data.can_assess} testid={`spec-notes-${c.code}`} onClosed={() => load()} />}
                {c.fields.length > 0 ? <div>{sortFields(c.fields).map((f) => renderFieldRow(f))}</div>
                  : (c.numbers || []).length === 0 && <p className="text-xs text-slate-400">No fields captured — Ask AI or add them in Vehicle Setup.</p>}
                {(c.numbers || []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-2 mt-1 border-t border-slate-50">
                    <span className="text-[11px] text-slate-400 mr-1">Tag numbers (move together on spawn):</span>
                    {c.numbers.map((n, i) => (<span key={i} data-testid={`comp-num-${c.code}-${i}`} className="text-[11px] font-mono bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 text-amber-800">{n.label}: {n.value}</span>))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Other specs (not tied to a component) */}
      {otherGroups.length > 0 && (
        <div className="space-y-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Other specs</div>
          {otherGroups.map((g) => (
            <div key={g.group} data-testid={`spec-group-${g.group}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{g.group}</span>
                {canEdit && g.fields.some(isDecodedPending) && (
                  <button data-testid={`spec-confirm-group-${g.group}`} onClick={() => askFor(g.fields, g.group)} className="text-[10px] text-emerald-600 hover:underline">confirm {fieldList(g.fields.filter(isDecodedPending).map((f) => f.label))}</button>
                )}
              </div>
              <div className="space-y-1.5">{sortFields(g.fields).map((f) => renderFieldRow(f))}</div>
            </div>
          ))}
        </div>
      )}

      {components.length === 0 && otherGroups.length === 0 && !descField && (
        <p className="text-sm text-slate-400">{needsCat ? "Set the vehicle category above (or Ask AI) to see its spec fields." : "No spec fields for this category yet — add them in Vehicle Setup."}</p>
      )}

      {canEdit && dirty && (
        <Button data-testid="specs-save-all" onClick={saveAll} disabled={saving} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save changes
        </Button>
      )}

      {/* full Ask AI workbench (replaces the old lighter scan dialog)
          REFRESH ON CLOSE, NOT ON EVERY SAVE. Telling this page to reload while the dialog is open
          tears the dialog down mid-job (the edit screen re-renders this panel, and the dialog's
          open flag lives here) — which is how a confirmed press looked like a dialog that
          vanished. Anything saved in there is picked up the moment it closes, however it closes:
          Done, the X, Escape or the overlay. */}
      {canScan && (
        <TruckAiWorkbench open={wbOpen} donorId={donorId} donor={donor}
          onOpenChange={(o) => {
            setWbOpen(o);
            if (!o) { setResult(null); load(); onCategoryChange?.(); }
          }}
          onDone={() => { setResult(null); load(); onCategoryChange?.(); }} />
      )}

      {/* confirm-all affirmation */}
      <AlertDialog open={!!confirmAll} onOpenChange={(o) => !o && setConfirmAll(null)}>
        <AlertDialogContent className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm {confirmAll?.words}?</AlertDialogTitle>
            <AlertDialogDescription>Only values decoded from the VIN are confirmed here. The engine, the transmission and every value the AI read are never confirmed together — each one needs its own ✓.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-all-yes" onClick={doConfirmAll} className="rounded-lg bg-emerald-600 hover:bg-emerald-700">Yes, confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {cpt && (
        <CreatePartFromTruck open={!!cpt} onOpenChange={(o) => !o && setCpt(null)} donorId={donorId} donorStock={donor?.stock_number}
          specKey={cpt.specKey} suggestedName={cpt.suggestedName} serialValue={cpt.serialValue}
          component={cpt.component} componentNumbers={cpt.componentNumbers} condition={cpt.condition} photoId={cpt.photoId}
          onCreated={() => { setCpt(null); load(); }} />
      )}
    </div>
  );
}
